import { Link } from "react-router-dom";
import { Compass, HeartHandshake, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const VALUES = [
    { icon: HeartHandshake, title: "Personal, always", text: "You speak with a named travel designer from the first call to the journey home — never a ticketing queue." },
    { icon: ShieldCheck, title: "Honest by design", text: "Itemised quotes, clear cancellation terms, no surge pricing. Trust is the only currency we trade in." },
    { icon: Sparkles, title: "Detail-obsessed", text: "The right room, the right tide for the backwater cruise, the right time at the Taj. Small things, chosen carefully." },
    { icon: Compass, title: "India first", text: "We specialise in domestic journeys — the subcontinent alone is a lifetime of travel, and we know it intimately." },
];

const About = () => (
    <>
        <PageHeader
            eyebrow="About Us"
            title="The story of Vistaaryatra"
            description="A young Indian travel company built on a classic promise: journeys planned with care, priced with honesty and supported around the clock."
        />

        <section className="py-24 lg:py-32" data-testid="about-story">
            <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
                <div>
                    <SectionHeading
                        eyebrow="Who We Are"
                        title="Travel should feel personal"
                        description="Vistaaryatra Pvt. Ltd. is a domestic tours and travel company. We were founded by passionate travellers who were tired of copy-paste itineraries and call-centre holidays — so we built the agency we always wanted to book with."
                    />
                    <SectionHeading
                        eyebrow="Our Mission"
                        title="Show you the India you imagined — and the parts you didn't know to ask for"
                        description="From golden forts to quiet backwaters, we craft tailor-made journeys with handpicked stays, transparent pricing and a 24/7 helpline that actually answers. Big enough to handle everything; small enough to care about everything."
                    />
                </div>
                <Reveal className="relative">
                    <div className="overflow-hidden rounded-2xl">
                        <img src={IMAGES.kerala} alt="Houseboat on the Kerala backwaters" className="h-[440px] w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[560px]" />
                    </div>
                    <div className="absolute -bottom-6 left-6 rounded-xl border border-brass/30 bg-navy px-7 py-5 shadow-xl">
                        <p className="font-serif text-2xl font-semibold text-brass">Tours &amp; Travels</p>
                        <p className="text-xs uppercase tracking-[0.25em] text-ivory/60">Across India</p>
                    </div>
                </Reveal>
            </div>
        </section>

        <section className="bg-cream py-24 lg:py-32" data-testid="about-values">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHeading eyebrow="What We Stand For" title="Four promises on every trip" align="center" />
                <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {VALUES.map((value, i) => (
                        <Reveal key={value.title} delay={i * 0.1} className="h-full">
                            <div className="card-frame flex h-full flex-col p-8 transition-all duration-300 hover:-translate-y-1.5">
                                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-brass/40 bg-brass-light/40 text-brass-dark">
                                    <value.icon className="h-5 w-5" />
                                </span>
                                <h3 className="mt-6 font-serif text-xl font-semibold text-navy">{value.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-steel">{value.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <section className="py-24 lg:py-32" data-testid="about-note">
            <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
                <Reveal>
                    <Compass className="mx-auto h-12 w-12 text-brass animate-slow-spin" strokeWidth={0.75} />
                    <blockquote className="mt-8 font-serif text-2xl font-medium italic leading-relaxed text-navy sm:text-3xl">
                        "We started Vistaaryatra with one belief — that a holiday is one of the few things you
                        buy, remember forever. Every itinerary we send out carries that weight."
                    </blockquote>
                    <p className="mt-8 text-xs font-semibold uppercase tracking-widest2 text-brass-dark">
                        The Founding Team · Vistaaryatra Pvt. Ltd.
                    </p>
                    <Link
                        to="/book"
                        data-testid="about-cta-button"
                        className="group mt-12 inline-flex items-center gap-2 rounded-full bg-brass px-8 py-4 text-sm font-semibold text-navy transition-all duration-300 hover:bg-brass-dark"
                    >
                        Travel with us
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </div>
        </section>
    </>
);

export default About;
